import { getPageSize } from '../utils/pageSize';
import { loadData } from '../utils/ApiUtil';
import { find, isString } from 'lodash';

export const buildUrl = (base, params = {}) => {
  const { includes, filters, sorts, page, query, fields } = params;
  const filterParams = getFilterParams(filters);
  const sortParams = getSortParams(sorts);
  const pageParams = getPageParams(page);
  const queryParams = getQueryParams(query);
  const fieldParams = getFieldParams(fields);

  const paramString = [
    `${filterParams}`,
    `${includes ? `include=${includes}` : ''}`,
    `${sortParams ? `sort=${sortParams}` : ''}`,
    `${pageParams ? `${pageParams}` : ''}`,
    `${queryParams ? `${queryParams}` : ''}`,
    `${fieldParams ? `${fieldParams}` : ''}`,
  ]
    .filter(Boolean)
    .join('&');

  return `${base}${base.indexOf('?') === -1 ? '?' : '&'}${paramString}`;
};

export const getFilterParams = filters => {
  if (!filters) return '';

  if (isString(filters)) return filters;

  return Object.keys(filters)
    .filter(key => filters[key] !== null && filters[key] !== undefined)
    .map(key => {
      if (filters[key] instanceof Array) {
        return filters[key].map(f => `filter[${key}][]=${encodeURIComponent(f)}`).join('&');
      } else {
        return `filter[${key}]=${encodeURIComponent(filters[key])}`;
      }
    })
    .join('&');
};

export const getSortParams = sorts => {
  if (!sorts) {
    return '';
  }
  if (!Array.isArray(sorts)) {
    sorts = [sorts];
  }
  return sorts
    .map(sort => {
      if (!sort) return '';
      return `${sort.direction === 'desc' ? '-' : ''}${sort.value}`;
    })
    .filter(Boolean)
    .join(',');
};

export const getPageParams = page => {
  if (typeof page === 'object' && page !== null) {
    page.size = page.size || getPageSize();
    page.number = page.number || 1;
  } else if (Number.isInteger(page)) {
    page = { number: page, size: getPageSize() };
  } else {
    page = { number: 1, size: getPageSize() };
  }

  return page ? `page[number]=${page.number || 1}&page[size]=${page.size}` : '';
};

export const getQueryParams = query => {
  if (!query) return '';

  return Object.entries(query)
    .filter(([_, value]) => value !== null && value !== undefined)
    .filter(([_, value]) => !!value.term)
    .map(([key, value]) => {
      let param = `query[${key}][term]=${encodeURIComponent(value.term)}`;
      const fields = Object.entries(value.fields)
        .map(([fieldKey, fieldValue]) => {
          return `${fieldKey}.${fieldValue}`;
        })
        .join(',');
      param += `&query[${key}][fields]=${fields}`;
      return param;
    })
    .join('&');
};

export const getFieldParams = fields => {
  if (!fields) return '';

  return Object.entries(fields)
    .filter(([_, value]) => value !== null && value !== undefined)
    .map(([key, value]) => `fields[${key}]=${value}`)
    .join('&');
};

export const getData = async (base, params = {}) => {
  const url = buildUrl(base, params);
  const { authToken } = params;
  let headers = {};
  if (authToken) {
    headers = {
      Authorization: `Bearer ${authToken}`,
    };
  }
  const json = await loadData(
    url,
    {
      headers,
      mode: 'cors',
    },
    false
  );
  const data = buildFromJsonApi(json);
  return {
    data,
    meta: json.meta,
  };
};

function buildFromJsonApi(json) {
  return buildDataFromJsonApi(json.data, json.included);
}

function buildDataFromJsonApi(data, included) {
  if (Array.isArray(data)) {
    return data.map(dataValue => buildDataObjectFromJsonApi(dataValue, included));
  }
  return buildDataObjectFromJsonApi(data, included);
}

function buildDataObjectFromJsonApi(data, included) {
  return Object.assign(
    { id: data.id },
    data.attributes,
    data.relationships && included
      ? Object.entries(data.relationships).reduce(
          (object, [key, value]) => ({
            ...object,
            [key]: buildRelationshipFromJsonApi(value.data, included),
          }),
          {}
        )
      : {}
  );
}

function buildRelationshipFromJsonApi(data, included) {
  if (!data) return;
  if (Array.isArray(data)) {
    return data.map(dataValue => buildRelationshipObjectFromJsonApi(dataValue, included));
  }
  return buildRelationshipObjectFromJsonApi(data, included);
}

function buildRelationshipObjectFromJsonApi(object, included) {
  const includedObject = find(included, object);
  if (!includedObject) {
    return null;
  }
  return buildDataFromJsonApi(includedObject, included);
}
